import html2canvas from 'html2canvas';
import RecordRTC from 'recordrtc';
import { useState } from 'react';

function PreviewCanvas({ topic, quote, caption, alignment, platform, visualMode, customImage, customAudio }) {
    const [downloading, setDownloading] = useState(false);
    const [progress, setProgress] = useState(0);

    // REAL Image Download
    const handleDownloadImage = async () => {
        const element = document.getElementById('sa-capture-area');
        if (!element) return;

        try {
            const canvas = await html2canvas(element, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
                scale: 2, 
            });
            
            const image = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = image;
            link.download = `limitless_${topic.replace(/\s+/g, '_').toLowerCase()}_post.png`;
            link.click();
        } catch (err) {
            console.error('Download failed:', err);
            alert('Failed to generate image download.');
        }
    };

    // REAL Video Generation using RecordRTC (Client-Side)
    const handleDownloadVideo = async () => {
        const element = document.getElementById('sa-capture-area');
        if (!element || !customAudio) return;

        setDownloading(true);
        setProgress(0);

        try {
            await new Promise(r => setTimeout(r, 200));

            const canvas = await html2canvas(element, {
                useCORS: true,
                allowTaint: true,
                scale: 1.5,
            });

            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const audioObj = new Audio(customAudio);
            audioObj.crossOrigin = "anonymous";
            
            const source = audioCtx.createMediaElementSource(audioObj);
            const destination = audioCtx.createMediaStreamDestination();
            source.connect(destination);
            source.connect(audioCtx.destination); 

            const recordCanvas = document.createElement('canvas');
            recordCanvas.width = canvas.width;
            recordCanvas.height = canvas.height;
            const ctx = recordCanvas.getContext('2d');
            ctx.drawImage(canvas, 0, 0);

            const canvasStream = recordCanvas.captureStream(30); 
            const combinedStream = new MediaStream([
                ...canvasStream.getVideoTracks(),
                ...destination.stream.getAudioTracks()
            ]);

            const recorder = new RecordRTC(combinedStream, {
                type: 'video',
                mimeType: 'video/mp4',
                bitsPerSecond: 12800000,
            });

            recorder.startRecording();
            audioObj.play();

            const duration = (audioObj.duration && audioObj.duration < 10) ? audioObj.duration * 1000 : 6000;
            const interval = 50; 
            let elapsed = 0;

            const timer = setInterval(() => {
                elapsed += interval;
                ctx.drawImage(canvas, 0, 0);
                setProgress(Math.min(Math.round((elapsed / duration) * 100), 100));
                
                if (elapsed >= duration) {
                    clearInterval(timer);
                    stopRecording(recorder, audioObj);
                }
            }, interval);

        } catch (err) {
            console.error('Video recording failed:', err);
            alert('Failed to start video recording.');
            setDownloading(false);
        }
    };

    const stopRecording = (recorder, audioObj) => {
        recorder.stopRecording(() => {
            const blob = recorder.getBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `limitless_${topic.replace(/\s+/g, '_').toLowerCase()}_video.mp4`;
            link.click();
            audioObj.pause();
            setDownloading(false);
            setProgress(0);
        });
    };

    const isDownloadReady = visualMode === 'generate' ? !!customImage : !!quote;

    return (
        <div className="sa-card" style={{ padding: 20 }}>
            <div className="sa-card__title">Live Preview</div>
            
            <div className="sa-preview-card" id="sa-capture-area">
                {customImage ? (
                    <img src={customImage} alt="Background" className="sa-preview-card__bg" crossOrigin="anonymous" />
                ) : (
                    <div className="sa-preview-card__bg" style={{ background: 'linear-gradient(45deg, #111, #222)' }} />
                )}
                
                <div className="sa-preview-card__content" style={{ justifyContent: 'center' }}>
                    {visualMode === 'upload' && quote && (
                        <div 
                            className="sa-preview-card__quote sa-studio-font"
                            style={{ textAlign: alignment }}
                            dangerouslySetInnerHTML={{ __html: quote }}
                        />
                    )}
                    {!isDownloadReady && (
                        <div className="sa-preview-card__placeholder">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                            </svg>
                            <span>{visualMode === 'generate' ? 'Generate a visual to see preview' : 'Generate a quote to see preview'}</span>
                        </div>
                    )}
                </div>
            </div>

            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button 
                  className="sa-btn sa-btn--publish" 
                  style={{ width: '100%', gap: 12 }}
                  disabled={!isDownloadReady}
                  onClick={handleDownloadImage}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download Post Image
                </button>

                <button 
                  className="sa-btn sa-btn--outline" 
                  style={{ width: '100%', gap: 12 }}
                  disabled={!isDownloadReady || downloading || (visualMode === 'upload' && !customAudio)}
                  onClick={handleDownloadVideo}
                >
                    {downloading ? (
                        <>
                          <div className="sa-spinner" style={{ width: 14, height: 14 }}></div>
                          Recording Video... {progress}%
                        </>
                    ) : (
                        <>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                            <line x1="7" y1="2" x2="7" y2="22" />
                            <line x1="17" y1="2" x2="17" y2="22" />
                            <line x1="2" y1="12" x2="22" y2="12" />
                            <line x1="2" y1="7" x2="7" y2="7" />
                            <line x1="2" y1="17" x2="7" y2="17" />
                            <line x1="17" y1="17" x2="22" y2="17" />
                            <line x1="17" y1="7" x2="22" y2="7" />
                          </svg>
                          Download with Music (MP4)
                        </>
                    )}
                </button>
                {!customAudio && visualMode === 'upload' && <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: 0 }}>* Upload audio to enable video download</p>}
                
                {downloading && (
                    <div style={{ 
                        height: 4, 
                        background: 'rgba(255,255,255,0.1)', 
                        borderRadius: 2, 
                        overflow: 'hidden'
                    }}>
                        <div style={{ 
                            width: `${progress}%`, 
                            height: '100%', 
                            background: 'var(--panacea-primary-color)',
                            transition: 'width 0.2s ease'
                        }} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default PreviewCanvas;
